import { Link } from "wouter";
import { trackContactCta } from "@/lib/analytics";
import { BrochureChapterLink } from "@/components/BrochureChapterLink";
import { useSeo, breadcrumbSchema, faqSchema, serviceSchema } from "@/lib/seo";
import { pageLastModified } from "@/data/pageDates";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowRight,
  Building2,
  SlidersHorizontal,
  Palette,
  FileText,
  Code2,
  BarChart3,
  Clock,
  ShieldAlert,
  Check,
  Minus,
  Tag,
} from "lucide-react";

const PATH = "/dealership-website-consent-tool";

const features = [
  {
    icon: Building2,
    title: "One workspace for every rooftop",
    body: "Keep each dealership or client account together: website domain, contact email, tracking tier, active status, tracking-control status, and audit notes. Create one or more banner instances per account.",
  },
  {
    icon: SlidersHorizontal,
    title: "Consent choices matched to tracking needs",
    body: "Three defined tiers set which optional categories visitors can manage. Necessary storage stays on; visitors accept, reject, or customize the rest when those controls are enabled.",
  },
  {
    icon: Palette,
    title: "A banner that fits each store",
    body: "Adjust position, colors, font, corner radius, headline and explanatory copy, button labels, and category names. Choose whether to show Reject All, Customize, and a floating preferences button.",
  },
  {
    icon: FileText,
    title: "Policies on sites without policy pages",
    body: "Show Privacy Policy and Terms & Conditions text in a popover straight from the banner, or keep using a Privacy Policy URL. Cookie Policy and Do Not Sell links point to existing URLs.",
  },
  {
    icon: Code2,
    title: "A deployment path that fits the site",
    body: "Every instance provides a direct script tag and a Google Tag Manager Custom HTML snippet, plus setup guidance. Preview the banner in the instance editor before you install.",
  },
  {
    icon: BarChart3,
    title: "Consent activity in view",
    body: "Review per-instance consent logs, and on Ads and Advanced tiers, engagement summaries for the banner itself, filtered by date range.",
  },
];

const tiers = [
  {
    name: "Basic",
    summary: "Necessary storage only.",
    fit: "For sites running only what the website needs to function.",
    cats: [true, false, false, false, false],
    analytics: false,
  },
  {
    name: "Ads",
    summary: "Necessary plus optional analytics, advertising, and personalization.",
    fit: "For stores running analytics and paid media through the site.",
    cats: [true, true, true, true, false],
    analytics: true,
  },
  {
    name: "Advanced",
    summary: "Everything in Ads, plus an identity-resolution category.",
    fit: "For setups that include identity-resolution tooling.",
    cats: [true, true, true, true, true],
    analytics: true,
  },
];

const categoryNames = ["Necessary", "Analytics", "Advertising", "Personalization", "Identity resolution"];

const steps = [
  { n: "01", title: "Organize accounts", body: "Add each dealership, rooftop, or client site with its domain and contacts." },
  { n: "02", title: "Choose a tier", body: "Basic, Ads, or Advanced sets the optional categories visitors can manage." },
  { n: "03", title: "Customize and preview", body: "Match the banner to the store's brand, set controls and policy text, then review it." },
  { n: "04", title: "Deploy", body: "Install via GTM on Consent Initialization – All Pages, or with a direct script tag." },
];

const guardrails = [
  "It is a consent-banner management and deployment tool. It is not legal advice and does not guarantee GDPR, CCPA, or any other legal compliance.",
  "It displays policy text you or your counsel supply. It does not write, review, or validate legal policies.",
  "It controls only scripts deployed through the configured GTM container or loaded after the consent script. It does not automatically block every hardcoded or externally loaded script.",
  "Analytics are banner and consent events recorded by the platform, not a website traffic or marketing-attribution report.",
  "Hashed IP and user-agent values are not a guarantee of anonymity.",
];

const faqs = [
  {
    q: "Does the Website User Consent Tool make our dealership compliant?",
    a: "No tool can promise that. It helps you configure, display, and record visitor consent choices and pass them to Google Consent Mode v2 and the dataLayer. Whether your setup meets your legal obligations is a question for your counsel.",
  },
  {
    q: "Can a dealer group or agency manage several websites?",
    a: "Yes. The workspace holds multiple client accounts, each with its own tracking tier, banner presentation, policy links or popover text, and deployment snippet. An account can have more than one banner instance.",
  },
  {
    q: "Will it block every tracking script on our site?",
    a: "No. It controls scripts deployed through the configured Google Tag Manager container or loaded after the consent script. Hardcoded or externally loaded scripts outside that path need to be moved or handled separately, which is something we can review during a walkthrough.",
  },
  {
    q: "How does it work with Google Tag Manager?",
    a: "Each instance provides a Custom HTML snippet with guidance to fire it on the Consent Initialization – All Pages trigger, so it runs before marketing tags. The script sets Google Consent Mode v2 defaults to denied, updates consent values after a visitor chooses, and pushes consent details to the dataLayer.",
  },
  {
    q: "What is stored in the consent logs?",
    a: "Each record includes the selected categories, timestamp, consent version, and website domain. IP address and user-agent values are hashed before storage and raw values are not stored. Hashing reduces exposure but is not a guarantee of anonymity.",
  },
  {
    q: "How long does a visitor's choice last?",
    a: "The default is 180 days and it is configurable per instance. Visitors may be prompted again when consent expires or when the instance configuration changes.",
  },
];

export default function DealershipWebsiteConsentTool() {
  useSeo({
    title: "Website User Consent Tool for Dealerships",
    description:
      "Relevant Dealer's Website User Consent Tool, powered by Relevant Consent: configure, brand, and deploy consent banners across dealership websites via GTM or script tag, with Consent Mode v2 and per-site logs.",
    canonicalPath: PATH,
    modifiedDate: pageLastModified(PATH),
    jsonLd: [
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Website User Consent Tool", path: PATH },
      ]),
      serviceSchema({
        name: "Website User Consent Tool, powered by Relevant Consent",
        description:
          "Consent-banner management and deployment for dealerships, dealer groups, and agencies: tiered consent categories, per-site customization, policy popovers, GTM or script deployment, Google Consent Mode v2 defaults, and per-instance consent logs.",
        slug: "dealership-website-consent-tool",
        category: "Consent Banner Management",
        urlPath: PATH,
      }),
      faqSchema(faqs),
    ],
  });

  return (
    <div className="pt-24 min-h-screen">
      {/* HERO */}
      <section className="bg-foreground text-white relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-20"
          style={{ backgroundImage: "radial-gradient(circle at 85% 10%, hsl(var(--brand)) 0%, transparent 45%)" }}
        />
        <div className="container mx-auto px-4 max-w-7xl py-20 md:py-28 relative grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
          <div>
            <p className="text-brand font-bold tracking-widest uppercase text-sm mb-5">Website User Consent Tool · powered by Relevant Consent</p>
            <h1 className="text-4xl md:text-6xl font-display font-bold text-white leading-[1.05] mb-6">
              Consent choices for every dealership website you run.
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-2xl mb-8 leading-relaxed">
              Configure and deploy branded consent banners for single rooftops, dealer groups, and the agencies behind them, from one workspace. Set categories by tier, install through Google Tag Manager or a script tag, and give shoppers a clear way to manage their preferences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-primary hover:bg-accent text-white font-bold h-14 px-8 text-base transition-colors">
                <Link href="/contact" onClick={() => trackContactCta("consent_hero")} data-testid="link-hero-walkthrough">
                  Request a walkthrough <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <a href="#guardrails" className="inline-flex items-center justify-center h-14 px-6 rounded-md border border-white/30 text-white font-semibold hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand transition-colors" data-testid="link-hero-guardrails">
                What it does not do
              </a>
            </div>
            <p className="mt-4 text-sm text-white/70">Walkthroughs are scheduled with our team. This is not a self-service signup.</p>
          </div>

          {/* Illustrative schematic, not a working demo */}
          <figure aria-label="Illustration of a consent banner layout" className="relative">
            <div className="rounded-2xl bg-white/5 border border-white/15 p-5">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-2.5 h-2.5 rounded-full bg-white/25" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/25" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/25" />
                <span className="ml-3 h-5 flex-1 rounded bg-white/10" />
              </div>
              <div className="grid grid-cols-3 gap-3 mb-5" aria-hidden="true">
                <div className="h-20 rounded-lg bg-white/10" />
                <div className="h-20 rounded-lg bg-white/10" />
                <div className="h-20 rounded-lg bg-white/10" />
              </div>
              <div className="rounded-xl bg-background text-foreground p-5 shadow-xl">
                <p className="font-display font-bold text-lg mb-1">Your privacy choices</p>
                <p className="text-sm text-muted-foreground mb-4">Headline, copy, colors, and buttons are set per store.</p>
                <ul className="space-y-2 mb-4 text-sm">
                  {["Necessary", "Analytics", "Advertising", "Personalization"].map((c, i) => (
                    <li key={c} className="flex items-center justify-between">
                      <span>{c}</span>
                      <span className={`w-9 h-5 rounded-full flex items-center px-0.5 ${i === 0 ? "bg-primary justify-end" : "bg-secondary border border-border"}`}>
                        <span className="w-4 h-4 rounded-full bg-white shadow" />
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2 text-xs font-semibold">
                  <span className="px-3 py-2 rounded-md bg-primary text-white">Accept All</span>
                  <span className="px-3 py-2 rounded-md border border-border">Reject All</span>
                  <span className="px-3 py-2 rounded-md border border-border">Customize</span>
                  <span className="px-3 py-2 underline underline-offset-2 text-primary">Privacy Policy</span>
                </div>
              </div>
            </div>
            <figcaption className="mt-3 text-xs text-white/60">Illustration only. Optional categories default to denied until a visitor chooses.</figcaption>
          </figure>
        </div>
      </section>

      {/* CHALLENGE */}
      <section className="py-20 md:py-24 border-b border-border">
        <div className="container mx-auto px-4 max-w-7xl grid lg:grid-cols-[1fr_1.3fr] gap-12">
          <div>
            <p className="text-primary font-bold tracking-widest uppercase text-sm mb-4">The real-world problem</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold">Every rooftop has a different site, stack, and set of tags.</h2>
          </div>
          <div className="space-y-5 text-lg text-muted-foreground leading-relaxed">
            <p>
              A dealer group might run a different website provider per brand, a different GTM container per store, and a different mix of analytics and ad pixels per market. The agency handling it all ends up managing consent one site at a time.
            </p>
            <p>
              Some platforms do not even let the store add a standalone privacy page. Meanwhile, a banner on its own does not decide what fires. What matters is how consent is configured, where the script loads, and which tags respect it.
            </p>
            <p className="text-foreground font-semibold">
              The Website User Consent Tool centralizes accounts and banner instances, gives each one configurable behavior, and hands you install code that fits the site.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-24 bg-secondary border-b border-border">
        <div className="container mx-auto px-4 max-w-7xl">
          <p className="text-primary font-bold tracking-widest uppercase text-sm mb-4">How it works</p>
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-12 max-w-2xl">From account to installed banner in four moves.</h2>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden border border-border">
            {steps.map((s) => (
              <li key={s.n} className="bg-background p-7 hover:bg-card transition-colors">
                <span className="font-display text-sm font-bold text-primary">{s.n}</span>
                <h3 className="text-xl font-display font-bold mt-3 mb-2">{s.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 md:py-24 border-b border-border">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-3xl mb-14">
            <p className="text-primary font-bold tracking-widest uppercase text-sm mb-4">Capabilities</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Built for teams managing more than one website.</h2>
            <p className="text-lg text-muted-foreground">Whether you run one store, twenty rooftops, or an agency book of dealer clients, each site keeps its own settings.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            {features.map((f) => (
              <div key={f.title} className="flex gap-5 group">
                <div className="w-12 h-12 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                  <f.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-display font-bold mb-2">{f.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="py-20 md:py-24 bg-secondary border-b border-border">
        <div className="container mx-auto px-4 max-w-7xl">
          <p className="text-primary font-bold tracking-widest uppercase text-sm mb-4">Tracking tiers</p>
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 max-w-2xl">Three tiers, in plain language.</h2>
          <p className="text-lg text-muted-foreground mb-12 max-w-3xl">Each account gets one tier. It defines which optional categories shoppers can accept, reject, or customize.</p>
          <div className="grid lg:grid-cols-3 gap-6">
            {tiers.map((t) => (
              <article key={t.name} className="bg-card rounded-2xl border border-border p-8 flex flex-col hover:border-primary/50 transition-colors" data-testid={`card-tier-${t.name.toLowerCase()}`}>
                <h3 className="text-2xl font-display font-bold mb-2">{t.name}</h3>
                <p className="font-semibold mb-1">{t.summary}</p>
                <p className="text-muted-foreground text-sm mb-6">{t.fit}</p>
                <ul className="space-y-2.5 mb-6 text-sm">
                  {categoryNames.map((c, i) => (
                    <li key={c} className={`flex items-center gap-2.5 ${t.cats[i] ? "text-foreground" : "text-muted-foreground"}`}>
                      {t.cats[i] ? <Check className="w-4 h-4 text-primary" /> : <Minus className="w-4 h-4" />}
                      <span>{c}{i === 0 && " (always on)"}</span>
                      <span className="sr-only">{t.cats[i] ? "included" : "not included"}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-auto pt-4 border-t border-border text-sm text-muted-foreground">
                  Engagement analytics tab: <span className="font-semibold text-foreground">{t.analytics ? "available" : "not included"}</span>
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* DEPLOYMENT */}
      <section className="py-20 md:py-24 bg-foreground text-white">
        <div className="container mx-auto px-4 max-w-7xl grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <p className="text-brand font-bold tracking-widest uppercase text-sm mb-4">Deployment</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">Install it the way the site already works.</h2>
            <div className="space-y-6 text-white/85 leading-relaxed">
              <div>
                <h3 className="text-xl font-display font-bold text-white mb-1 flex items-center gap-2"><Tag className="w-5 h-5 text-brand" />Google Tag Manager</h3>
                <p>Paste the Custom HTML snippet and fire it on the Consent Initialization – All Pages trigger so it runs before marketing tags.</p>
              </div>
              <div>
                <h3 className="text-xl font-display font-bold text-white mb-1 flex items-center gap-2"><Code2 className="w-5 h-5 text-brand" />Direct script tag</h3>
                <p>For sites where GTM is not an option, add the script tag provided for that instance.</p>
              </div>
              <div>
                <h3 className="text-xl font-display font-bold text-white mb-1 flex items-center gap-2"><Clock className="w-5 h-5 text-brand" />Consent duration</h3>
                <p>Choices last 180 days by default, configurable per instance. Shoppers may be re-prompted on expiry or after a configuration change.</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-white/15 bg-white/5 p-7">
            <h3 className="text-lg font-display font-bold text-white mb-5">What the generated script does</h3>
            <ol className="space-y-5">
              {[
                ["Sets defaults", "Google Consent Mode v2 values start as denied for optional categories."],
                ["Listens for a choice", "Accept, reject, or saved custom preferences from the banner."],
                ["Updates consent", "Consent Mode values are updated from the visitor's choice."],
                ["Pushes to the dataLayer", "Consent details are pushed so configured tags can respond."],
              ].map(([h, b], i) => (
                <li key={h} className="flex gap-4">
                  <span className="w-8 h-8 shrink-0 rounded-full border border-brand text-brand font-display font-bold text-sm flex items-center justify-center">{i + 1}</span>
                  <div>
                    <p className="font-semibold text-white">{h}</p>
                    <p className="text-white/75 text-sm">{b}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-6 text-xs text-white/65">Vendor tags must be set up to respect Consent Mode or dataLayer signals. Built-in integrations with every vendor are not claimed.</p>
          </div>
        </div>
      </section>

      {/* RECORDS + POLICIES */}
      <section className="py-20 md:py-24 border-b border-border">
        <div className="container mx-auto px-4 max-w-7xl grid lg:grid-cols-2 gap-8">
          <div className="rounded-2xl border border-border bg-card p-8">
            <p className="text-primary font-bold tracking-widest uppercase text-sm mb-3">Records & engagement</p>
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">Per-instance consent logs.</h2>
            <p className="text-muted-foreground mb-5">Each record holds the selected categories, timestamp, consent version, and website domain. IP address and user-agent are hashed before storage; raw values are not kept. Hashing is a safeguard, not an anonymity guarantee.</p>
            <p className="font-semibold mb-2">Ads and Advanced tiers add engagement analytics:</p>
            <ul className="grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
              {["Banner impressions", "Accept-all choices", "Reject-all choices", "Customization opens", "Saved preferences", "Total button clicks", "Acceptance rate", "Date-range filtering"].map((x) => (
                <li key={x} className="flex items-center gap-2"><Check className="w-4 h-4 text-primary shrink-0" />{x}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-card p-8">
            <p className="text-primary font-bold tracking-widest uppercase text-sm mb-3">Policy popovers</p>
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">For sites that cannot add a policy page.</h2>
            <p className="text-muted-foreground mb-4">Many dealer website platforms limit custom pages. Enter Privacy Policy and Terms & Conditions text in the instance settings and it opens in a popover from the banner. When popover text is supplied, the Privacy Policy link uses it; otherwise a Privacy Policy URL works.</p>
            <p className="text-muted-foreground">Cookie Policy and Do Not Sell links point to existing URLs. The text shown is what you or your counsel provide; the tool does not write or validate it.</p>
          </div>
        </div>
      </section>

      {/* GUARDRAILS */}
      <section id="guardrails" className="py-20 md:py-24 bg-secondary border-b border-border scroll-mt-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center gap-3 mb-4">
            <ShieldAlert className="w-6 h-6 text-primary" />
            <p className="text-primary font-bold tracking-widest uppercase text-sm">Straight talk</p>
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">A banner alone does not solve compliance.</h2>
          <p className="text-lg text-muted-foreground mb-8">We would rather you know the limits up front.</p>
          <ul className="space-y-4">
            {guardrails.map((g) => (
              <li key={g} className="flex gap-4 bg-background border-l-4 border-primary rounded-r-xl p-5 text-foreground leading-relaxed">{g}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ + CTA */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-8 text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full mb-16">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-lg font-bold" data-testid={`button-faq-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="bg-foreground text-white rounded-3xl p-10 text-center">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-4">See how it fits your stores.</h2>
            <p className="text-white/80 mb-8 max-w-lg mx-auto">
              Tell us about your websites, GTM setup, and tracking. We will walk through the workspace and talk honestly about what it can and cannot control on your sites.
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-accent text-white font-bold h-12 px-8 transition-colors">
              <Link href="/contact" onClick={() => trackContactCta("consent_final")} data-testid="link-final-walkthrough">Request a walkthrough</Link>
            </Button>
            <p className="mt-4 text-sm text-white/70">A conversation with our team, not an instant signup.</p>
            <div className="mt-6"><BrochureChapterLink chapter={14} placement="consent" tone="dark" label="Consent choices in our brochure" /></div>
          </div>
        </div>
      </section>
    </div>
  );
}
